import dagre from 'dagre';
import { EDGE_TYPES } from './CanvasConstants';
import { getElements } from './CanvasUtil';
import { createSaveEvent } from './createSaveEvent';
import { saveCanvasEvent } from './canvasApi';
import { isLegacyForm } from './formTypeConstants';

export const IGNORED_LINKS_FOR_DAGRE = [
  EDGE_TYPES.LOOPLINK,
  EDGE_TYPES.FORMREDIRECTLINK,
  EDGE_TYPES.NOTE_LINK,
];

const NOTE_NODE_TYPE = 'note';
const COMPONENT_HANDLE_POSITION = 16;

export const applyDagreLayouting = (nodesItems, edges) => {
  const DagreGraph = new dagre.graphlib.Graph();
  DagreGraph.setDefaultEdgeLabel(() => ({}));
  const graphSetup = {
    rankdir: 'LR', // Left -> Right structure
    nodesep: 200, // Increased from 150 - Horizontal node spacing in px
    ranksep: 200, // Increased from 150 - Vertical node spacing in px
    ranker: 'network-simplex', // Use network simplex algorithm for better edge routing
    align: 'UL', // Aligns nodes to minimize crossing
  };

  // Separate note nodes from other nodes (db nodes participate in layout)
  const noteNodes = nodesItems.filter(node => node.type === NOTE_NODE_TYPE);
  const regularNodes = nodesItems.filter(node => node.type !== NOTE_NODE_TYPE);

  const nodes = [];
  for (let i = 0; i < regularNodes.length; i++) {
    const node = regularNodes[i];
    nodes.push({
      ...node,
      width: calculateNodeWidthOrHeight(node, true),
      height: calculateNodeWidthOrHeight(node, false),
    });
  }

  // Pass in the desired graph setup
  DagreGraph.setGraph(graphSetup);

  // Set the nodes in the dagre graph
  nodes.forEach(node => {
    DagreGraph.setNode(node.id, { width: node.width, height: node.height });
  });

  edges.forEach(edge => {
    if (!IGNORED_LINKS_FOR_DAGRE.includes(edge.type)) {
      DagreGraph.setEdge(edge.source, edge.target);
    }
  });

  // Execute the layout
  dagre.layout(DagreGraph);

  nodes.forEach(node => {
    const nodeWithPosition = DagreGraph.node(node.id);
    node.targetPosition = 'left';
    node.sourcePosition = 'right';

    const noteHandlePosition = node.height / 2;
    const nodeHandlePosition =
      node.type === NOTE_NODE_TYPE ? noteHandlePosition : COMPONENT_HANDLE_POSITION;

    // We are shifting the dagre node position (anchor=center center) to the top left
    // so it matches the React Flow node anchor point (top left).
    node.position = {
      x: nodeWithPosition.x - node.width / 2,
      y: nodeWithPosition.y - nodeHandlePosition,
    };

    return node;
  });

  const optimizedEdges = optimizeEdgePaths(edges, nodes);

  return [...nodes, ...noteNodes, ...optimizedEdges];
};

const optimizeEdgePaths = (edges, nodes) => {
  const nodeMap = {};
  nodes.forEach(node => {
    nodeMap[node.id] = node;
  });

  return edges.map(edge => {
    if (IGNORED_LINKS_FOR_DAGRE.includes(edge.type)) {
      return edge;
    }

    const sourceNode = nodeMap[edge.source];
    const targetNode = nodeMap[edge.target];

    if (!sourceNode || !targetNode) {
      return edge;
    }

    const verticalDistance = Math.abs(targetNode.position.y - sourceNode.position.y);

    // If nodes are far apart vertically, use a more curved path
    let edgeType = 'default';
    let curvature = 0;

    if (verticalDistance > 200) {
      edgeType = 'smoothstep';

      curvature = Math.min(0.5, verticalDistance / 1000);

      if (targetNode.position.y < sourceNode.position.y) {
        curvature *= -1;
      }
    }

    return {
      ...edge,
      edgeType: edgeType,
      curvature: curvature,
      animated: false,
      style: {
        ...edge.style,
        strokeLinejoin: 'round',
        strokeLinecap: 'round',
      },
    };
  });
};

const NODE_HEIGHT_PADDING = 20; // Extra padding to prevent overlap

const calculateNodeWidthOrHeight = (node, isWidth) => {
  if (isWidth) {
    const defaultWidth = 250;
    // Use measured DOM width if available
    if (node && node.measured?.width) return node.measured.width;
    if (node && node.width) return node.width;
    return defaultWidth;
  } else {
    const defaultHeight = 150;

    // Use measured DOM height if available (already includes description/instructions)
    if (node && node.measured?.height) return node.measured.height + NODE_HEIGHT_PADDING;
    if (node && node.height) return node.height + NODE_HEIGHT_PADDING;

    // Fallback: estimate height based on content
    let estimatedHeight = defaultHeight;
    if (node?.data?.description) estimatedHeight += 60;
    if (node?.data?.instructions) estimatedHeight += 60;
    return estimatedHeight;
  }
};

// Returns true if the icon is larger than the standard icon size (i.e: the forms)
const isLargerIcon = node => {
  try {
    let type = node.data.type;
    return isLegacyForm(type) || type === 'bulk_assessment';
  } catch (e) {
    return false;
  }
};

// Grabs the size of the canvas
export const getCanvasSizeInfo = () => {
  let res = {
    width: 0,
    height: 0,
  };

  try {
    let info = document.getElementsByClassName('react-flow__pane')[0].getBoundingClientRect();
    if (info.width && info.height) res = info;
    return res;
  } catch (e) {
    return res;
  }
};

export const DEFAULT_ZOOM_FOR_FOCUSING_VIEWPORT = 1.25;

/**
 *
 * @param {*} targetPosition => The position of the component we want to focus on
 * @param {*} xOffset => This dictates how far left/right the viewport will move
 * @param {*} rfInstance => The react flow instance
 * @param {*} reuseZoom => true/false on whether to use the previous canvas zoom or use the default
 * @param {*} projectId => The project's id
 * @param {*} remainingCanvasWidthAsDecimal => Is a decimal representation of the remaining width of the canvas
 *                                             (i.e: 100% - 40% (PANE_WIDTH) = 0.6)
 * @returns
 */
export const setPositionInCanvasView = async (
  targetViewport,
  xOffset,
  rfInstance,
  reuseZoom,
  projectId,
  remainingCanvasWidthAsDecimal,
  targetZoom
) => {
  if (!rfInstance || !targetViewport) return;

  let info = getCanvasSizeInfo();

  // Set defaults
  if (!xOffset) xOffset = -100;
  if (!remainingCanvasWidthAsDecimal) remainingCanvasWidthAsDecimal = 0.6;

  let zoom = targetZoom ? targetZoom : DEFAULT_ZOOM_FOR_FOCUSING_VIEWPORT;

  // If we're re-using the zoom, set the zoom from the react flow instance
  if (reuseZoom) zoom = rfInstance.toObject().viewport.zoom;

  const x = rfInstance.toObject().viewport.x;
  const y = rfInstance.toObject().viewport.y;

  // Calculates the viewport center
  const viewPortCenterX = info.width / 2;
  const viewPortCenterY = info.height / 2;

  // Calculate the new viewport center considering the node's coordinates
  const newViewPortCenterX = viewPortCenterX - targetViewport.x * zoom;
  const newViewPortCenterY = viewPortCenterY - targetViewport.y * zoom;

  // Calculate the far left X position
  const farLeftX = newViewPortCenterX - viewPortCenterX;

  // Calculate the first quadrant line of the canvas
  let canvasQuarter = (remainingCanvasWidthAsDecimal * info.width) / 2;

  const newViewport = {
    x: farLeftX + canvasQuarter + xOffset,
    y: newViewPortCenterY,
    zoom: zoom,
  };

  rfInstance.setViewport(
    { x: newViewport.x, y: newViewport.y, zoom: newViewport.zoom },
    { duration: 1000 }
  );

  // If a project id is given, we want to save the viewport in the backend
  if (projectId) await saveViewport(newViewport, rfInstance, projectId);
};

const saveViewport = async (viewport, reactFlowInstance, projectId) => {
  let saveEvent = createSaveEvent(
    'SAVE_VIEWPORT',
    projectId,
    viewport,
    getElements(reactFlowInstance),
    { projectId: projectId }
  );

  // Send the save viewport event
  const json = await saveCanvasEvent(projectId, saveEvent);
};
